import React, { useState, useMemo, useEffect } from 'react';
import { AppCard, AppPage } from '../../components/general';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { confirmPopup } from 'primereact/confirmpopup';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import { FeeHudLineViewModel, FeeRulesVersionViewModel, CreateOrUpdateFeeHudLineViewModel, ExcelImportResult } from '../../models/fees';
import HudLineEditorDialog from './HudLineEditorDialog';
import { ImportHudLinesDialog } from './ImportHudLinesDialog';

interface HudLinesProps {
  hudLines: FeeHudLineViewModel[];
  versions: FeeRulesVersionViewModel[];
  loading: boolean;
  selectedVersionId: number | null;
  onVersionChange: (versionId: number | null) => void;
  onCreateClick: () => void;
  onEditClick: (hudLine: FeeHudLineViewModel) => void;
  onDeleteClick: (hudLineId: number) => void;
  onImport: (file: File) => Promise<ExcelImportResult>;
  onBackClick: () => void;
  isEditorOpen: boolean;
  selectedHudLine: FeeHudLineViewModel | null;
  onSave: (payload: CreateOrUpdateFeeHudLineViewModel) => Promise<void>;
  onCancelEdit: () => void;
}

const HudLines: React.FC<HudLinesProps> = ({
  hudLines,
  versions,
  loading,
  selectedVersionId,
  onVersionChange,
  onCreateClick,
  onEditClick,
  onDeleteClick,
  onImport,
  onBackClick,
  isEditorOpen,
  selectedHudLine,
  onSave,
  onCancelEdit,
}) => {
  const [search, setSearch] = useState('');
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [isCompactView, setIsCompactView] = useState(false);
  const [sortField, setSortField] = useState<string>('hudLine');
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0 | null | undefined>(1);
  const [showImportDialog, setShowImportDialog] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsCompactView(window.innerWidth <= 1140);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const versionOptions = useMemo(() => {
    const options = versions
      .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0))
      .map((version) => ({
        label: `${version.versionNumber}${version.description ? ` - ${version.description}` : ''}`,
        value: version.feeRulesVersionId,
      }));
    return [{ label: 'Draft', value: null }, ...options];
  }, [versions]);

  const filtered = useMemo(() => {
    if (!hudLines) return [];

    return hudLines.filter((hudLine) => {
      const matchesSearch =
        !search ||
        hudLine.hudLine?.toLowerCase().includes(search.toLowerCase()) ||
        hudLine.description?.toLowerCase().includes(search.toLowerCase());

      return matchesSearch;
    });
  }, [hudLines, search]);

  const handleDeleteConfirm = (event: React.MouseEvent<HTMLButtonElement>, hudLineId: number, isDraft: boolean) => {
    if (!isDraft) {
      return;
    }

    confirmPopup({
      target: event.currentTarget as HTMLElement,
      message: 'Are you sure you want to delete this HUD Line?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => onDeleteClick(hudLineId),
    });
  };

  const hudLineTemplate = (rowData: FeeHudLineViewModel) => {
    return rowData.hudLine || '-';
  };

  const descriptionTemplate = (rowData: FeeHudLineViewModel) => {
    return (
      <div
        style={{
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: '1.5',
        }}
      >
        {rowData.description || '-'}
      </div>
    );
  };

  const versionTemplate = (rowData: FeeHudLineViewModel) => {
    return rowData.feeRulesVersionId ? rowData.feeRulesVersionId.toString() : 'Draft';
  };

  const actionsTemplate = (rowData: FeeHudLineViewModel) => {
    const isDraft = !rowData.feeRulesVersionId;

    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => onEditClick(rowData)}
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger"
          onClick={(e) => handleDeleteConfirm(e, rowData.feeHudLineId, isDraft)}
          disabled={!isDraft}
          style={!isDraft ? { opacity: 0.3 } : undefined}
          tooltip={isDraft ? 'Delete' : 'Cannot delete published HUD Line'}
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  const footer = (
    <div className="flex justify-content-between align-items-center">
      <span className="text-sm">
        Showing {Math.min(first + 1, filtered.length)}-{Math.min(first + rows, filtered.length)} of{' '}
        {filtered.length}
      </span>
    </div>
  );

  if (loading) {
    return (
      <AppPage title="HUD Lines" breadcrumbs="Admin - Fees - HUD Lines">
        <AppCard>
          <div className="flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <ProgressSpinner />
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <>
      <AppPage 
        title="HUD Lines" 
        breadcrumbs="Admin - Fees - HUD Lines"
        headerEnd={
          <div className="flex align-items-center gap-2">
            <label className="text-sm text-gray-600 whitespace-nowrap flex items-center">Version:</label>
            <Dropdown
              value={selectedVersionId}
              options={versionOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => onVersionChange(e.value)}
              placeholder="Select Version"
              className="w-80 border-1 border-solid border-gray-300"
            />
          </div>
        }
      >
        <AppCard>
          <div className="flex justify-content-between align-items-center gap-2 flex-wrap mb-4">
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search HUD Lines..."
                className="p-inputtext-sm border-1 border-solid border-gray-300"
              />
            </span>
            <div className="flex gap-2 align-items-center">
              <Button
                label="← Back"
                className="p-button-link p-button-sm"
                onClick={onBackClick}
              />
              <Tooltip target=".create-hud-btn" />
              <Button
                icon="pi pi-plus"
                label={!isCompactView ? 'Create HUD Line' : undefined}
                className="p-button-sm p-button-primary create-hud-btn"
                onClick={onCreateClick}
                disabled={selectedVersionId !== null}
                style={{
                  ...(selectedVersionId !== null ? { opacity: 0.3 } : {}),
                  ...(isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : {}),
                }}
                tooltip={isCompactView ? 'Create HUD Line' : undefined}
                tooltipOptions={{ position: 'bottom' }}
              />
              <Tooltip target=".import-hud-btn" />
              <Button
                icon="pi pi-upload"
                label={!isCompactView ? 'Import' : undefined}
                className="p-button-sm p-button-secondary import-hud-btn"
                onClick={() => setShowImportDialog(true)}
                disabled={selectedVersionId !== null}
                style={{
                  ...(selectedVersionId !== null ? { opacity: 0.3 } : {}),
                  ...(isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : {}),
                }}
                tooltip={isCompactView ? 'Import' : undefined}
                tooltipOptions={{ position: 'bottom' }}
              />
            </div>
          </div>
          <DataTable
            value={filtered}
            paginator
            rows={rows}
            first={first}
            onPage={(e) => {
              setFirst(e.first);
              setRows(e.rows);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            footer={footer}
            emptyMessage="No HUD Lines found"
            sortMode="single"
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={(e) => {
              setSortField(e.sortField);
              setSortOrder(e.sortOrder);
            }}
            removableSort
            className="p-datatable-sm p-datatable-gridlines"
            resizableColumns
            columnResizeMode="fit"
          >
            <Column field="hudLine" header="HUD Line" body={hudLineTemplate} sortable style={{ minWidth: '120px' }} />
            <Column
              field="description"
              header="Description"
              body={descriptionTemplate}
              sortable
              style={{ minWidth: '200px' }}
            />
            <Column
              field="feeRulesVersionId"
              header="Fee Rule Version"
              body={versionTemplate}
              style={{ minWidth: '150px' }}
            />
            <Column header="Actions" body={actionsTemplate} style={{ minWidth: '120px', width: '120px' }} />
          </DataTable>
        </AppCard>
      </AppPage>

      {isEditorOpen && (
        <HudLineEditorDialog
          visible={isEditorOpen}
          hudLine={selectedHudLine}
          onSave={onSave}
          onCancel={onCancelEdit}
        />
      )}

      <ImportHudLinesDialog
        visible={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImport={onImport}
      />
    </>
  );
};

export default HudLines;

